/**
 * Synthetic seed data. We model the SHAPE of the OpenWells corpus, never ship
 * its values. Two well sets — US Bakken/Williston (imperial vernacular) and
 * LAM Vaca Muerta/Neuquina (metric, Secretaría de Energía vocabulary) — faithful
 * to each basin. Shapes mirror the live M/P wire contracts exactly (snake_case,
 * WellOut / EventOut / DailyReportOut / pivots), so MSW is a drop-in stand-in
 * for the real services.
 */
import type {
  WellOut,
  EventOut,
  DailyReportOut,
  CostPivotRow,
  PhaseVariance,
  InboxItem,
  ResolvedCatalogOut,
  EntryOut,
  SavedQueryOut,
} from "@api/types";

const NOW = "2026-07-18T06:00:00Z";
const ORG = "org-demo";

function well(
  id: string,
  o: {
    uwi: string;
    api14: string | null;
    common_name: string;
    field: string;
    operator: string;
    status: string;
    basin: string;
    purpose: string;
    reservoir_type: string;
    formation: string;
    lat: number;
    lon: number;
    td_m: number;
    rig?: string;
  },
): WellOut {
  return {
    id,
    organization_id: ORG,
    uwi: o.uwi,
    api14: o.api14,
    common_name: o.common_name,
    site: o.field,
    field: o.field,
    operator: o.operator,
    status: o.status,
    sync_status: "synced",
    last_sync: NOW,
    source: "openwells",
    properties: {
      basin: o.basin,
      purpose: o.purpose,
      reservoir_type: o.reservoir_type,
      formation: o.formation,
      lat: o.lat,
      lon: o.lon,
      td_m: o.td_m,
      rig: o.rig,
    },
    is_active: true,
    created_at: NOW,
    created_by: "sync",
    modified_at: NOW,
    modified_by: "sync",
  };
}

export const WELLS: WellOut[] = [
  // ── Set A · US · Williston / Bakken (imperial vernacular) ──
  well("us-1", {
    uwi: "33053024130000",
    api14: "33-053-02413",
    common_name: "Nesson 24-13H",
    field: "Nesson Anticline",
    operator: "Meridian Resources",
    status: "drilling",
    basin: "Williston",
    purpose: "development",
    reservoir_type: "unconventional",
    formation: "Bakken",
    lat: 48.11,
    lon: -103.02,
    td_m: 6340,
    rig: "H&P FlexRig 447",
  }),
  well("us-2", {
    uwi: "33061008050000",
    api14: "33-061-00805",
    common_name: "Three Forks 8-5H",
    field: "Sanish",
    operator: "Meridian Resources",
    status: "completion",
    basin: "Williston",
    purpose: "development",
    reservoir_type: "unconventional",
    formation: "Three Forks",
    lat: 47.98,
    lon: -102.71,
    td_m: 6100,
    rig: "Cyclone 214",
  }),
  well("us-3", {
    uwi: "33025001110000",
    api14: "33-025-00111",
    common_name: "Bakken State 1-11",
    field: "Parshall",
    operator: "Northern Plains E&P",
    status: "producing",
    basin: "Williston",
    purpose: "exploration",
    reservoir_type: "unconventional",
    formation: "Bakken",
    lat: 47.83,
    lon: -102.34,
    td_m: 5980,
    rig: "Patterson 291",
  }),
  // ── Set B · LAM · Vaca Muerta / Neuquina (metric, SE vocabulary) ──
  well("ar-1", {
    uwi: "AR-NQ-FP-1216",
    api14: null,
    common_name: "FP.Nq-1216(h)",
    field: "Fortín de Piedra",
    operator: "Petrolera Andina",
    status: "drilling",
    basin: "Neuquina",
    purpose: "development",
    reservoir_type: "unconventional",
    formation: "Vaca Muerta",
    lat: -37.65,
    lon: -68.94,
    td_m: 3147,
    rig: "DLS-167",
  }),
  well("ar-2", {
    uwi: "AR-NQ-ET-2021",
    api14: null,
    common_name: "ET.xp-2021",
    field: "El Trapial",
    operator: "Cuyana Oil",
    status: "completion",
    basin: "Neuquina",
    purpose: "appraisal",
    reservoir_type: "unconventional",
    formation: "Vaca Muerta",
    lat: -38.02,
    lon: -69.51,
    td_m: 3360,
    rig: "Ensign 32",
  }),
  well("ar-3", {
    uwi: "AR-NQ-EFO-334",
    api14: null,
    common_name: "EFO.Nq-334",
    field: "Est. Fernández Oro",
    operator: "Río Negro Energía",
    status: "producing",
    basin: "Neuquina",
    purpose: "development",
    reservoir_type: "conventional",
    formation: "Sierras Blancas",
    lat: -38.94,
    lon: -67.9,
    td_m: 2890,
    rig: "AESA 41",
  }),
  well("ar-4", {
    uwi: "AR-CH-GSJ-77",
    api14: null,
    common_name: "GSJ.Ch-77",
    field: "Cañadón Seco",
    operator: "Patagonia Petróleo",
    status: "suspended",
    basin: "Golfo San Jorge",
    purpose: "development",
    reservoir_type: "conventional",
    formation: "Comodoro Rivadavia",
    lat: -46.55,
    lon: -67.6,
    td_m: 2410,
    rig: "PAE 09",
  }),
];

/** Lookup by well id AND by uwi (P endpoints key on UWI). */
export const WELL_BY_ID: Record<string, WellOut> = Object.fromEntries(WELLS.map((w) => [w.id, w]));
export const WELL_BY_UWI: Record<string, WellOut> = Object.fromEntries(
  WELLS.map((w) => [w.uwi, w]),
);

// ── Events (jobs) — keyed by well UWI ──
function ev(o: Partial<EventOut> & Pick<EventOut, "id" | "well_uwi" | "event_type">): EventOut {
  return {
    wellbore_uwi: `${o.well_uwi}/1`,
    api_no: null,
    event_code: null,
    phase_entry_key: null,
    objective_1: null,
    date_ops_start: null,
    date_ops_end: null,
    status_end: null,
    ...o,
  };
}

export const EVENTS: EventOut[] = [
  ev({
    id: "evt-us1-drl",
    well_uwi: "33053024130000",
    api_no: "33-053-02413",
    event_type: "drilling",
    event_code: "001",
    phase_entry_key: "lateral",
    objective_1: 'Drill 6⅛" lateral to TD 20,300 ft',
    date_ops_start: "2026-07-04",
    status_end: null,
    unit_profile_key: "api",
    cost_authorized_currency: "USD",
  }),
  ev({
    id: "evt-us1-cmp",
    well_uwi: "33053024130000",
    api_no: "33-053-02413",
    event_type: "completion",
    event_code: "002",
    objective_1: "Multi-stage frac — 40 stages",
    date_ops_start: null,
    unit_profile_key: "api",
    cost_authorized_currency: "USD",
  }),
  ev({
    id: "evt-ar1-drl",
    well_uwi: "AR-NQ-FP-1216",
    event_type: "drilling",
    event_code: "001",
    phase_entry_key: "intermedia",
    objective_1: "Perforar sección intermedia hasta punto de casing",
    date_ops_start: "2026-07-07",
    unit_profile_key: "metric",
    cost_authorized_currency: "USD",
  }),
];

export const EVENTS_BY_WELL_UWI: Record<string, EventOut[]> = EVENTS.reduce(
  (acc, e) => {
    (acc[e.well_uwi] ??= []).push(e);
    return acc;
  },
  {} as Record<string, EventOut[]>,
);

// ── Daily reports — keyed by event, with server-computed KPIs baked in ──
function report(
  o: Partial<DailyReportOut> &
    Pick<DailyReportOut, "id" | "event_id" | "well_uwi" | "report_no" | "date_report">,
): DailyReportOut {
  return {
    comment_summary: null,
    comment_forecast_24: null,
    supervisor_1_entry_key: null,
    md_current: null,
    tvd_current: null,
    state: "open",
    is_last_of_event: false,
    days_total: null,
    days_op: null,
    pct_complete: null,
    rop_avg_day: null,
    daily_cost: null,
    ...o,
  };
}

export const REPORTS: DailyReportOut[] = [
  report({
    id: "ddr-us1-14",
    event_id: "evt-us1-drl",
    well_uwi: "33053024130000",
    report_no: 14,
    date_report: "2026-07-18",
    state: "open",
    is_last_of_event: true,
    supervisor_1_entry_key: "sup.halvorsen",
    unit_profile_key: "api", // US well — stored in FEET (store-as-entered)
    currency: "USD",
    md_current: 18450, // ft, as entered (NOT metres)
    tvd_current: 10320, // ft
    days_total: 14,
    days_op: 14,
    pct_complete: 61,
    rop_avg_day: 42, // ft/hr, as entered
    daily_cost: 142380,
    comment_summary:
      'MIRU complete 0600. Drilled 6⅛" lateral 17,890→18,450 ft (560 ft, avg ROP 42 ft/hr). Circulated bottoms-up, no gas shows. Surveyed at 18,000 ft — 89.4° inc, on plan. POOH to change bit (run #2). RIH new BHA, resumed rotary 2100 hrs. MW steady 10.4 ppg.',
    comment_forecast_24:
      'Continue drilling 6⅛" lateral to planned TD 20,300 ft. Gyro survey run prior to TD. Circulate and condition mud ahead of wiper trip. Prep for completion crew handover within 48 hrs.',
  }),
  report({
    id: "ddr-us1-13",
    event_id: "evt-us1-drl",
    well_uwi: "33053024130000",
    report_no: 13,
    date_report: "2026-07-17",
    state: "closed",
    supervisor_1_entry_key: "sup.halvorsen",
    unit_profile_key: "api",
    currency: "USD",
    md_current: 17890, // ft
    tvd_current: 10320, // ft
    days_total: 13,
    days_op: 13,
    pct_complete: 57,
    rop_avg_day: 44, // ft/hr
    daily_cost: 131050,
    comment_summary: "Drilled lateral 17,330→17,890 ft. Slid 2 stands correcting azimuth.",
    comment_forecast_24: "Continue lateral. Bit change anticipated near 18,400 ft.",
  }),
  report({
    id: "ddr-ar1-11",
    event_id: "evt-ar1-drl",
    well_uwi: "AR-NQ-FP-1216",
    report_no: 11,
    date_report: "2026-07-18",
    state: "open",
    is_last_of_event: true,
    supervisor_1_entry_key: "sup.gimenez",
    unit_profile_key: "metric", // AR well — stored in METRES
    currency: "USD",
    md_current: 2480, // m, as entered
    tvd_current: 2510, // m
    days_total: 11,
    days_op: 11,
    pct_complete: 46,
    rop_avg_day: 22.1, // m/hr
    daily_cost: 96200,
    comment_summary:
      "Perforando sección intermedia. Sin NPT en el turno. Peso del lodo 1,25 sg estable.",
    comment_forecast_24: "Continuar perforación rotativa hasta punto de casing (~2.700 m).",
  }),
];

export const REPORTS_BY_ID: Record<string, DailyReportOut> = Object.fromEntries(
  REPORTS.map((r) => [r.id, r]),
);
export const REPORTS_BY_EVENT: Record<string, DailyReportOut[]> = REPORTS.reduce(
  (acc, r) => {
    (acc[r.event_id] ??= []).push(r);
    return acc;
  },
  {} as Record<string, DailyReportOut[]>,
);

// ── Cost pivots per event, by dimension ──
type PivotSet = Record<string, CostPivotRow[]>;
export const COST_PIVOTS: Record<string, PivotSet> = {
  "evt-us1-drl": {
    vendor: [
      { key: "H&P FlexRig 447", total: 4198000 },
      { key: "Dakota Directional Svcs", total: 2610400 },
      { key: "Basin Trucking LLC", total: 350000 },
      { key: "MudTech Fluids", total: 400000 },
    ],
    phase: [
      { key: "Mob / demob", total: 350000 },
      { key: "Surface hole", total: 618000 },
      { key: "Intermediate", total: 1684000 },
      { key: "Production", total: 1896000 },
      { key: "Lateral", total: 2610400 },
    ],
    cost_class: [
      { key: "Day-rate", total: 4198000 },
      { key: "Footage", total: 2610400 },
      { key: "Consumables", total: 400000 },
      { key: "Mobilization", total: 350000 },
    ],
  },
  "evt-ar1-drl": {
    vendor: [
      { key: "DLS Argentina", total: 1980000 },
      { key: "Baker Hughes Arg", total: 640000 },
      { key: "Transporte Neuquén", total: 210000 },
    ],
    phase: [
      { key: "Superficie", total: 420000 },
      { key: "Intermedia", total: 1980000 },
      { key: "Conductor", total: 430000 },
    ],
    cost_class: [
      { key: "Tarifa diaria", total: 2410000 },
      { key: "Consumibles", total: 420000 },
    ],
  },
};

// ── Plan-vs-actual per event ──
export const PLAN_VS_ACTUAL: Record<string, { by_phase: PhaseVariance[] }> = {
  "evt-us1-drl": {
    by_phase: [
      { phase: "Mob / demob", afe: 400000, actual: 350000, variance: -50000 },
      { phase: "Surface hole", afe: 650000, actual: 618000, variance: -32000 },
      { phase: "Intermediate", afe: 1520000, actual: 1684000, variance: 164000 },
      { phase: "Production", afe: 1980000, actual: 1896000, variance: -84000 },
      { phase: "Lateral", afe: 2340000, actual: 2610400, variance: 270400 },
      { phase: "Completion", afe: 1510000, actual: 400000, variance: -1110000 },
    ],
  },
  "evt-ar1-drl": {
    by_phase: [
      { phase: "Superficie", afe: 450000, actual: 420000, variance: -30000 },
      { phase: "Intermedia", afe: 1850000, actual: 1980000, variance: 130000 },
      { phase: "Conductor", afe: 400000, actual: 430000, variance: 30000 },
    ],
  },
};

// ── NPT roll-ups per event ──
export const NPT_ROLLUPS: Record<
  string,
  { total_hours: number; npt_hours: number; by_class: CostPivotRow[] }
> = {
  "evt-us1-drl": {
    total_hours: 336,
    npt_hours: 41.5,
    by_class: [
      { key: "Downhole (mechanical)", total: 18.5 },
      { key: "Surface equipment", total: 9.0 },
      { key: "Weather", total: 6.5 },
      { key: "Waiting on cement", total: 4.5 },
      { key: "Directional", total: 3.0 },
    ],
  },
  "evt-ar1-drl": {
    total_hours: 264,
    npt_hours: 12.0,
    by_class: [
      { key: "Equipo de superficie", total: 6.0 },
      { key: "Espera por materiales", total: 4.0 },
      { key: "Clima", total: 2.0 },
    ],
  },
};

// ── Inbox (cross-well daily-event queue) ──
const INBOX_FROM_REPORTS: InboxItem[] = REPORTS.map((r) => ({
  daily_report_id: r.id,
  event_id: r.event_id,
  well_uwi: r.well_uwi,
  event_type: EVENTS.find((e) => e.id === r.event_id)?.event_type ?? "drilling",
  date_report: r.date_report,
  state: r.state,
  supervisor_1_entry_key: r.supervisor_1_entry_key,
}));

// A deeper synthetic feed so the cross-well Inbox has enough rows to exercise
// its filters + cursor pagination in standalone (MSW) mode. These reference the
// same wells/events as the seeded reports but stand in for older report days.
const INBOX_SYNTH_SPECS: Array<
  Pick<InboxItem, "well_uwi" | "event_id" | "event_type" | "state" | "supervisor_1_entry_key"> & {
    offset: number;
  }
> = [
  { well_uwi: "33053024130000", event_id: "evt-us1-drl", event_type: "drilling", state: "open", supervisor_1_entry_key: "sup.halvorsen", offset: 3 },
  { well_uwi: "33061008050000", event_id: "evt-us1-drl", event_type: "drilling", state: "closed", supervisor_1_entry_key: "sup.halvorsen", offset: 3 },
  { well_uwi: "AR-NQ-FP-1216", event_id: "evt-ar1-drl", event_type: "completion", state: "open", supervisor_1_entry_key: "sup.gimenez", offset: 4 },
  { well_uwi: "33025001110000", event_id: "evt-us1-cmp", event_type: "completion", state: "open", supervisor_1_entry_key: "sup.halvorsen", offset: 4 },
  { well_uwi: "AR-NQ-ET-2021", event_id: "evt-ar1-drl", event_type: "workover", state: "closed", supervisor_1_entry_key: "sup.gimenez", offset: 5 },
  { well_uwi: "33061008050000", event_id: "evt-us1-drl", event_type: "intervention", state: "closed", supervisor_1_entry_key: "sup.halvorsen", offset: 6 },
  { well_uwi: "AR-NQ-EFO-334", event_id: "evt-ar1-drl", event_type: "drilling", state: "open", supervisor_1_entry_key: "sup.gimenez", offset: 6 },
  { well_uwi: "33053024130000", event_id: "evt-us1-drl", event_type: "drilling", state: "closed", supervisor_1_entry_key: "sup.halvorsen", offset: 7 },
  { well_uwi: "AR-CH-GSJ-77", event_id: "evt-ar1-drl", event_type: "workover", state: "open", supervisor_1_entry_key: "sup.gimenez", offset: 8 },
  { well_uwi: "33025001110000", event_id: "evt-us1-cmp", event_type: "completion", state: "closed", supervisor_1_entry_key: "sup.halvorsen", offset: 9 },
  { well_uwi: "AR-NQ-FP-1216", event_id: "evt-ar1-drl", event_type: "intervention", state: "open", supervisor_1_entry_key: "sup.gimenez", offset: 10 },
  { well_uwi: "33061008050000", event_id: "evt-us1-drl", event_type: "drilling", state: "closed", supervisor_1_entry_key: "sup.halvorsen", offset: 11 },
  { well_uwi: "AR-NQ-ET-2021", event_id: "evt-ar1-drl", event_type: "completion", state: "closed", supervisor_1_entry_key: "sup.gimenez", offset: 12 },
  { well_uwi: "33053024130000", event_id: "evt-us1-drl", event_type: "drilling", state: "open", supervisor_1_entry_key: "sup.halvorsen", offset: 13 },
  { well_uwi: "AR-NQ-EFO-334", event_id: "evt-ar1-drl", event_type: "workover", state: "closed", supervisor_1_entry_key: "sup.gimenez", offset: 14 },
  { well_uwi: "33025001110000", event_id: "evt-us1-drl", event_type: "drilling", state: "closed", supervisor_1_entry_key: "sup.halvorsen", offset: 15 },
  { well_uwi: "AR-CH-GSJ-77", event_id: "evt-ar1-drl", event_type: "intervention", state: "closed", supervisor_1_entry_key: "sup.gimenez", offset: 16 },
  { well_uwi: "33061008050000", event_id: "evt-us1-cmp", event_type: "completion", state: "closed", supervisor_1_entry_key: "sup.halvorsen", offset: 18 },
  { well_uwi: "AR-NQ-FP-1216", event_id: "evt-ar1-drl", event_type: "drilling", state: "closed", supervisor_1_entry_key: "sup.gimenez", offset: 20 },
  { well_uwi: "33053024130000", event_id: "evt-us1-drl", event_type: "workover", state: "closed", supervisor_1_entry_key: "sup.halvorsen", offset: 22 },
  { well_uwi: "AR-NQ-ET-2021", event_id: "evt-ar1-drl", event_type: "drilling", state: "closed", supervisor_1_entry_key: "sup.gimenez", offset: 24 },
  { well_uwi: "33025001110000", event_id: "evt-us1-cmp", event_type: "completion", state: "closed", supervisor_1_entry_key: "sup.halvorsen", offset: 26 },
];

/** Subtract `days` from a YYYY-MM-DD date, returning YYYY-MM-DD. */
function daysBefore(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

const INBOX_ANCHOR = "2026-07-18";

const INBOX_SYNTH: InboxItem[] = INBOX_SYNTH_SPECS.map((s, idx) => ({
  daily_report_id: `synth-inbox-${idx + 1}`,
  event_id: s.event_id,
  well_uwi: s.well_uwi,
  event_type: s.event_type,
  date_report: daysBefore(INBOX_ANCHOR, s.offset),
  state: s.state,
  supervisor_1_entry_key: s.supervisor_1_entry_key,
}));

export const INBOX: InboxItem[] = [...INBOX_FROM_REPORTS, ...INBOX_SYNTH];

// ── A couple of resolved catalogs (activity codes, cost classes) ──
function entry(category: string, key: string, label: string): EntryOut {
  return {
    id: `${category}:${key}`,
    category_key: category,
    organization_id: ORG,
    site_region_key: null,
    entry_key: key,
    display_label: label,
    aliases: null,
    properties: null,
    is_active: true,
    superseded_by: null,
    valid_from: null,
    valid_to: null,
    created_at: NOW,
    created_by: "trustee",
    modified_at: NOW,
    modified_by: "trustee",
  };
}

export const CATALOGS: Record<string, ResolvedCatalogOut> = {
  activity_codes: {
    category_key: "activity_codes",
    generation: "gen-1",
    entries: [
      entry("activity_codes", "DRLG", "Drilling"),
      entry("activity_codes", "TRIP", "Tripping"),
      entry("activity_codes", "CSG", "Casing"),
      entry("activity_codes", "CMT", "Cementing"),
      entry("activity_codes", "SURV", "Survey"),
      entry("activity_codes", "NPT", "Non-productive time"),
    ],
  },
  cost_classes: {
    category_key: "cost_classes",
    generation: "gen-1",
    entries: [
      entry("cost_classes", "day_rate", "Day-rate"),
      entry("cost_classes", "footage", "Footage"),
      entry("cost_classes", "lump_sum", "Lump-sum"),
      entry("cost_classes", "consumables", "Consumables"),
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────
// Saved queries (§4). Mirrors the Saved Reports wireframe library. `spec` is
// the FE-owned versioned shape {v:1,target,params}. The dev user
// (…0000000000d0) owns the "mine" rows; the rest are other authors' public
// rows so the All/Mine/Public filter has something to bite on.
// ─────────────────────────────────────────────────────────────────────────
const DEV_USER = "00000000-0000-0000-0000-0000000000d0";
const USER_JT = "00000000-0000-0000-0000-0000000000j7";
const USER_DS = "00000000-0000-0000-0000-0000000000d5";

export const SAVED_QUERIES: SavedQueryOut[] = [
  {
    id: "sq-open-drilling",
    name: "Open drilling reports — all wells",
    description: "Every open daily report across accessible wells, newest first. Default triage view.",
    spec: { v: 1, target: "inbox", params: { state: "open", sort: "-date_report" } },
    visibility: "public",
    owner_user_id: DEV_USER,
  },
  {
    id: "sq-npt-4h",
    name: "NPT > 4h this month",
    description: "Non-productive time events longer than 4 hours, current month, all rigs.",
    spec: { v: 1, target: "time-npt", params: { min_hours: 4, period: "month" } },
    visibility: "private",
    owner_user_id: DEV_USER,
  },
  {
    id: "sq-cost-vendor-redhawk",
    name: "Cost by vendor — Redhawk",
    description: "Cost/AFE pivot filtered to Redhawk pad wells, grouped by vendor and cost class.",
    spec: {
      v: 1,
      target: "cost-afe",
      params: { pad: "Redhawk", group_by: ["vendor", "cost_class"], date_range: "event" },
    },
    visibility: "public",
    owner_user_id: USER_JT,
  },
  {
    id: "sq-cost-fp",
    name: "Fortín de Piedra — completion cost pivot",
    description: "Completion-phase costs for the Fortín de Piedra pad, by well and stage.",
    spec: {
      v: 1,
      target: "cost-afe",
      params: { pad: "Fortin de Piedra", phase: "completion", group_by: ["well", "stage"] },
    },
    visibility: "private",
    owner_user_id: DEV_USER,
  },
  {
    id: "sq-dls",
    name: "DLS > 3°/100ft — active wells",
    description: "Survey stations with dogleg severity above 3°/100ft across wells currently drilling.",
    spec: { v: 1, target: "surveys", params: { min_dls: 3, status: "drilling" } },
    visibility: "private",
    owner_user_id: USER_DS,
  },
  {
    id: "sq-bit-perf",
    name: "Bakken pad — weekly bit performance",
    description: "BHA/bit runs with ROP and dull grade, last 7 days, Bakken wells only.",
    spec: { v: 1, target: "bha", params: { basin: "Bakken", period: "week" } },
    visibility: "public",
    owner_user_id: USER_JT,
  },
  {
    id: "sq-mud-overrun",
    name: "Mud cost overruns — Vaca Muerta",
    description: "Daily mud product cost above program budget, Vaca Muerta wells, last 30 days.",
    spec: { v: 1, target: "mud", params: { basin: "Vaca Muerta", overrun: true, period: "30d" } },
    visibility: "private",
    owner_user_id: DEV_USER,
  },
  {
    id: "sq-awaiting-close",
    name: "Awaiting close > 24h",
    description: "Reports left in open state for more than one day, all wells, sorted by age.",
    spec: { v: 1, target: "inbox", params: { state: "open", min_age_hours: 24, sort: "-age" } },
    visibility: "public",
    owner_user_id: USER_JT,
  },
  {
    id: "sq-casing-running",
    name: "Casing running — Bakken 3-well pad",
    description:
      "Tubulars strings and pipe tally progress for the 3-well Bakken pad currently running casing.",
    spec: { v: 1, target: "tubulars", params: { basin: "Bakken", running: "casing" } },
    visibility: "private",
    owner_user_id: USER_DS,
  },
];
